import Dexie, { type Table } from "dexie";

export type PunchStatus = "pending" | "syncing" | "synced" | "failed" | "auth_required";

export type OutboxPunch = {
  /** Auto-increment primary key — doubles as the FIFO sync order. */
  id?: number;
  /** Client-generated UUID sent to the server as client_uuid for idempotent replays. */
  uuid: string;
  /** Owner — punches are only ever synced under the user who recorded them. */
  userId: number;
  action: "clock_in" | "clock_out";
  /**
   * Every scalar form field exactly as captured at punch time (current_time,
   * tz_name, gps_*, submitted_ts, task, notes, ...). Never recomputed at sync
   * time so the original recorded instant is preserved.
   */
  fields: Record<string, string>;
  /** Compressed selfie JPEG; nulled after successful sync to reclaim quota. */
  selfieBlob: Blob | null;
  selfieName: string;
  /** Turnstile token captured at punch time, only usable while fresh. */
  turnstileToken: string | null;
  tokenIssuedAt: number | null;
  status: PunchStatus;
  attempts: number;
  lastError: string | null;
  lastErrorStatus: number | null;
  /** Epoch ms before which automatic sync skips this punch (backoff gate). */
  nextAttemptAt: number;
  createdAt: number;
  syncedAt: number | null;
  serverEntryId: number | null;
};

export type KvRecord = { key: string; value: unknown };

/**
 * Outbox item for standalone field notes (modeled directly on OutboxPunch for reuse of
 * status machine, enqueue, syncEngine drain, broadcast, prune, auth_required, etc.).
 * No selfieBlob or turnstile (field notes never used Turnstile in the form).
 * `fields` holds the exact payload captured at submit time (additional_note, correction_*,
 * reason, original/corrected datetimes, and client `submitted_at`).
 */
export type OutboxFieldNote = {
  /** Auto-increment primary key — doubles as FIFO order within field notes. */
  id?: number;
  /** Client-generated UUID sent as client_uuid for idempotent replays/dedup. */
  uuid: string;
  /** Owner (only notes for the current logged-in user are ever synced). */
  userId: number;
  /** The exact create payload (preserves client datetimes etc. for offline). */
  fields: Record<string, string | null | undefined>;
  status: PunchStatus;
  attempts: number;
  lastError: string | null;
  lastErrorStatus: number | null;
  /** Epoch ms before which automatic sync skips this note (backoff gate). */
  nextAttemptAt: number;
  createdAt: number;
  syncedAt: number | null;
  serverNoteId: number | null;
};

class PunchclockDB extends Dexie {
  outbox!: Table<OutboxPunch, number>;
  kv!: Table<KvRecord, string>;
  // Parallel store for field notes (keeps punch outbox untouched; shares status + helpers).
  fieldNotesOutbox!: Table<OutboxFieldNote, number>;

  constructor() {
    // cache: "disabled" avoids Dexie's optimistic-ops path that throws
    // "null is not an object (evaluating '*.trans'|*.type')" under Safari Private
    // browsing when IndexedDB transactions are ephemeral/unreliable.
    super("punchclock-offline", { cache: "disabled" });
    this.version(2).stores({
      outbox: "++id, uuid, status, userId, [userId+status], createdAt",
      kv: "key",
      fieldNotesOutbox: "++id, uuid, status, userId, createdAt",
    });
  }
}

export const db = new PunchclockDB();
